import React from 'react';
import { Button, Menu, Text } from '@mantine/core';
import { ChevronDown, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ProjectDetail } from '@tinypro-audit/contracts';

const sourceId = (value: unknown): string | null => {
  const id =
    typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return /^[1-9]\d{0,31}$/.test(id) ? id : null;
};
export function preSiteReportHref(
  jobId: unknown,
  projectId: string,
): string | null {
  const job = sourceId(jobId);
  return job
    ? `/audits/${encodeURIComponent(job)}?project=${encodeURIComponent(projectId)}`
    : null;
}

export function PreSiteReportLink({
  jobId,
  projectId,
}: {
  jobId: unknown;
  projectId: string;
}) {
  const href = preSiteReportHref(jobId, projectId);
  if (!href) return null;
  return (
    <Button
      component={Link}
      to={href}
      variant="default"
      size="sm"
      leftSection={<FileText size={15} />}
      title="Forms, estimates and the latest pre-site form review for this job"
    >
      View pre-site report · Job {String(jobId)}
    </Button>
  );
}

export function ProjectPreSiteReports({ data }: { data: ProjectDetail }) {
  const jobs = [
    ...new Set(
      data.workflow.assessments
        .map((assessment) => sourceId(assessment.provenance.job_id))
        .filter((id): id is string => id !== null),
    ),
  ];
  if (!jobs.length) return null;
  if (jobs.length === 1)
    return (
      <PreSiteReportLink
        jobId={jobs[0]}
        projectId={data.project.st_project_id}
      />
    );
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          variant="default"
          size="sm"
          leftSection={<FileText size={15} />}
          rightSection={<ChevronDown size={15} />}
        >
          Pre-site reports ({jobs.length})
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Latest stored report for each job</Menu.Label>
        {jobs.map((job) => (
          <Menu.Item
            key={job}
            component={Link}
            to={preSiteReportHref(job, data.project.st_project_id)!}
            leftSection={<FileText size={15} />}
          >
            <Text size="sm">Job {job}</Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
